import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { glueDimensionValues } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import type Block from 'webapp/models-custom/block';
import DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import type DsDataRootTopLevel from 'webapp/models-custom/ds-data/root/top';
import Serviced from 'webapp/models-custom/serviced';

// what a story element shows about a metric (its source, and name / attribute / unit)
export interface StoryMetricData {
	source: { avatar: string; name: string };
	metric: { name: string; attribute: string; unit: string };
}

// data-select
export interface CompatibleStoryModel {
	dataStoryColor: number;
	toJSON: () => object;
	data: object | null;
	dataRoot: DsDataRootTopLevel | null;
	isMissingMetric: boolean;
	shownTitle: string;
	dateRange: DateRangeInterface.FormulaInterface;
	id?: string | null; // for advanced
	block?: Block | null; // for advanced
	firstCompare?: DsCompare;
	isNew?: boolean;
	isDeletedState?: () => boolean;
}

export default abstract class StoryElementBase extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	abstract type: string; // one of enums/ReportSlideStoryElementType
	abstract get longTitle(): string;
	abstract fillElementContent(node: HTMLElement): void;
	abstract openEdit(node: HTMLElement): void;

	model: CompatibleStoryModel;
	isEditable = false;

	constructor({ model }: { model: CompatibleStoryModel }) {
		super();
		this.model = model;
	}

	// this will change when something important changes
	// component/story-elements/base includes this in template
	// and on change will trigger the updateAttributes on the story-element node
	get dataHash() {
		const model = this.model;
		if (model?.isDeletedState?.()) {
			return -1;
		} else {
			const data = {
				col: this.color,
				ds: model?.toJSON(),
				data: model?.data,
				lo: this.isLoading,
				del: this.isMissingOrDeleted,
				em: this.isEmpty,
			};
			return hashCode(JSON.stringify(data));
		}
	}

	get color() {
		return this.model.dataStoryColor;
	}

	get isEmpty() {
		return !!this.model.isMissingMetric;
	}

	get isMissingOrDeleted() {
		const deletedStateCheck =
			this.model.isDeletedState ||
			function () {
				return false;
			};
		return deletedStateCheck();
	}

	get isLoading(): boolean {
		return this.model.isNew || (!this.isEmpty && !this.model.data);
	}

	get dataRoot() {
		return this.model.dataRoot || null;
	}

	@cached
	get dataOutputs() {
		return this.dataRoot?.outputs || [];
	}

	get dataOutput(): DsDataOutputTopLevel | null {
		return this.dataOutputs[0] || null;
	}

	@cached
	get dataPoint() {
		const output = this.dataOutput;
		if (output) {
			return this.mmSamplesService.createDChartPointFromPoint(output, output.point);
		} else {
			return null;
		}
	}

	get comparePoint() {
		return this.dataPoint?.comparePoints[0] || null;
	}

	get primaryMetricData(): StoryMetricData | null {
		const dataRoot = this.dataRoot,
			dataOutput = this.dataOutput;

		return {
			source: {
				avatar: dataRoot?.sourceAvatar || '',
				name: dataRoot?.sourceName || '',
			},
			metric: {
				name: this.model.shownTitle,
				// read from data, in case multiples are returned
				attribute: glueDimensionValues(dataOutput?.dimensionValues),
				unit: dataOutput?.unit || '',
			},
		};
	}

	@cached
	get dateRange() {
		let dateRange;

		const dateRangeFormula = this.model.dateRange;
		if (dateRangeFormula) {
			dateRange = DateRange.createFromFormula(dateRangeFormula);
		}
		return dateRange || null;
	}

	get compareMetricData(): StoryMetricData | null {
		// only used if compare is with another metric
		let metricData;

		const compareCM = this.compare;
		if (compareCM?.isOtherMetric) {
			const compareOutput = this.dataRoot?.compareRoots[0]?.outputs[0];

			const metricSetup = compareCM.metricSetup;

			const attribute = metricSetup.firstAttributeNameFromBasicFilters,
				unit = metricSetup.specificSelectedUnit;

			metricData = {
				source: {
					avatar: compareOutput?.sourceAvatar || '',
					name: compareOutput?.sourceName || '',
				},
				metric: {
					name: compareOutput?.metricName || 'unknown',
					attribute,
					unit,
				},
			};
		}

		return metricData || null;
	}

	get compare() {
		return this.model.firstCompare || null;
	}

	get isDataIncomplete(): boolean {
		// checking statusObjects of type: sync
		let isSync = false;

		const dataRoot = this.dataRoot;
		if (dataRoot) {
			const statusObjects = dataRoot.statusObjects;
			isSync = statusObjects.some((so) => so.isSync);
		}

		return isSync;
	}

	get metricText(): string {
		const metricData = this.primaryMetricData;

		if (metricData) {
			return this.getMetricText(metricData);
		} else {
			return '';
		}
	}

	get shortTitle(): string {
		return this.longTitle;
	}

	addLayoutForSourceAndMetric(
		node: HTMLElement,
		metricData: StoryMetricData,
		shortLayout = false,
	): void {
		const metricText = this.getMetricText(metricData),
			sourceAvatar = metricData.source.avatar,
			sourceName = metricData.source.name;

		if (sourceAvatar) {
			const imgNode = document.createElement('img');
			imgNode.src = sourceAvatar;

			const emWithImageNode = document.createElement('em');
			emWithImageNode.append(imgNode);

			if (shortLayout) {
				node.append(emWithImageNode, metricText);
			} else {
				emWithImageNode.append(sourceName);
				const emMetricNode = document.createElement('em');
				emMetricNode.append(metricText);
				node.append(emMetricNode, ' from ', emWithImageNode);
			}
		} else {
			node.append(metricText);
		}
	}

	openAdvanced(): void {
		// make a copy of block bcos of fresh blockSetup and data-select instances
		const blockRecord = this.model.block?.model;

		if (!blockRecord) {
			return;
		}

		const block = this.mmBlocksService.createModelFromRecord(blockRecord),
			blockSetup = block.blockSetup,
			dataSelect = blockSetup.dataSelects.find((ds) => ds.id === this.model.id);

		if (dataSelect) {
			blockSetup.isAutosaveOn = false; // in advanced, saves with button

			// when in reports, we open advanced only if block panel is already open
			// and the report components will take care of the opening panel or opening advanced
			const isTriggered = this.eventManagerService.trigger(
				AppEvent.DATA_STORY_ELEMENT_OPEN_ADVANCED,
				{
					block,
					dataSelect,
				},
			);
			if (!isTriggered) {
				this.mmDataSelectsService.openAdvancedPopup({
					dataSelect,
					blockSetup,
					sourceForTracking: 'story-element',
				});
			}
		}
	}

	private getMetricText(metricData: StoryMetricData): string {
		const metricName = metricData.metric.name,
			metricAttribute = metricData.metric.attribute,
			metricUnit = metricData.metric.unit;

		let metricText = metricName;
		if (metricAttribute) {
			metricText += ` / ${metricAttribute}`;
		}
		if (metricUnit) {
			metricText += ` / ${metricUnit}`;
		}

		return metricText;
	}
}
